/**
 * Batch search keeps Shopify's regular search untouched and only takes over
 * queries that contain an explicit separator (OR, semicolon or newline). Comma
 * lists are accepted only when every item looks like an identifier, so regular
 * punctuation in text searches keeps Shopify's native behavior.
 *
 * Each term is searched independently. This deliberately avoids guessing
 * whether a value lives in a product tag, handle, SKU, barcode or another
 * field indexed by Shopify.
 */
(function () {
  'use strict';

  const EXPLICIT_BATCH_SEPARATOR = /(?:\s+OR\s+|[;\r\n]+)/i;
  const COMMA_SEPARATOR = /,+/;
  const REQUEST_CONCURRENCY = 4;
  const NATIVE_SEARCH_FIELDS = ['tag', 'variants.sku', 'variants.barcode', 'title'];
  const NATIVE_FIELD_PREFIX = /^(?:tag|title|product_type|vendor|variants\.(?:sku|barcode|title)):/i;

  function parseTerms(value) {
    const query = String(value || '').trim();

    if (!query) return [];

    let parts;
    if (EXPLICIT_BATCH_SEPARATOR.test(query)) {
      parts = query.split(EXPLICIT_BATCH_SEPARATOR);
    } else if (query.includes(',')) {
      const commaParts = query.split(COMMA_SEPARATOR).map((part) => part.trim());
      const looksLikeIdentifierList = commaParts.every(
        (part) => part && !/\s/.test(part) && /\d/.test(part)
      );

      // A normal text search such as "gömlek, pantolon" must stay native.
      if (!looksLikeIdentifierList) return [];
      parts = commaParts;
    } else {
      return [];
    }

    const terms = [];

    parts.forEach((part) => {
      const term = part.trim();

      if (!term) return;

      terms.push(term);
    });

    return terms.length > 1 ? terms : [];
  }

  function createSearchUrl(action, query, useClientFallback = false) {
    const url = new URL(action, window.location.origin);
    url.search = '';
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    if (useClientFallback) url.searchParams.set('batch', '1');
    return url;
  }

  function uniqueTerms(terms) {
    const seen = new Set();

    return terms.filter((term) => {
      const key = term.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function isNativeScopedQuery(terms) {
    return terms.length > 1 && terms.every((term) => NATIVE_FIELD_PREFIX.test(term));
  }

  function formatSearchValue(value) {
    if (/^[A-Za-z0-9._-]+$/.test(value)) return value;
    return `"${value.replace(/([\\"])/g, '\\$1')}"`;
  }

  function getNativeFieldCandidates(term) {
    if (/^\d{8,}$/.test(term)) {
      return ['variants.barcode', 'tag', 'variants.sku', 'title'];
    }

    return NATIVE_SEARCH_FIELDS;
  }

  async function fieldHasProducts(field, term) {
    const route = window.theme?.routes?.predictive_search_url || '/search/suggest';
    const url = new URL(route, window.location.origin);
    url.pathname = `${url.pathname.replace(/\.json$/, '')}.json`;
    url.searchParams.set('q', `${field}:${formatSearchValue(term)}`);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '1');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,vendor,variants.title,variants.sku,tag,variants.barcode'
    );

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`Search field probe failed with status ${response.status}`);

    const data = await response.json();
    return (data.resources?.results?.products?.length || 0) > 0;
  }

  async function resolveNativeTerm(term) {
    if (NATIVE_FIELD_PREFIX.test(term)) return term;

    for (const field of getNativeFieldCandidates(term)) {
      if (await fieldHasProducts(field, term)) {
        return `${field}:${formatSearchValue(term)}`;
      }
    }

    return null;
  }

  async function resolveNativeQuery(terms) {
    const distinctTerms = uniqueTerms(terms);
    const results = await mapWithConcurrency(distinctTerms, resolveNativeTerm);

    if (results.some((result) => result.status !== 'fulfilled' || !result.value)) return null;
    return results.map((result) => result.value).join(' OR ');
  }

  async function mapWithConcurrency(items, mapper, concurrency = REQUEST_CONCURRENCY) {
    const results = new Array(items.length);
    let nextIndex = 0;

    async function worker() {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;

        try {
          results[index] = {
            status: 'fulfilled',
            value: await mapper(items[index], index),
          };
        } catch (reason) {
          results[index] = { status: 'rejected', reason };
        }
      }
    }

    const workerCount = Math.min(concurrency, items.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    return results;
  }

  function getProductKey(element) {
    const link = element.querySelector(
      '.product-card-title[href], .product-card--image-link[href], a[href*="/products/"]'
    );

    if (!link) return '';

    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      const productPath = url.pathname.match(/\/products\/([^/]+)/i);
      return (productPath ? productPath[1] : url.pathname).toLowerCase();
    } catch (error) {
      return link.getAttribute('href').split('?')[0].toLowerCase();
    }
  }

  function collectUniqueElements(documents, selector) {
    const seen = new Set();
    const elements = [];

    documents.forEach((documentItem) => {
      documentItem.querySelectorAll(selector).forEach((element) => {
        const key = getProductKey(element);

        if (!key || seen.has(key)) return;

        seen.add(key);
        elements.push(element);
      });
    });

    return elements;
  }

  function setBatchSearchLoading(root, loading) {
    const productGridContainer = root.querySelector('#ProductGridContainer');
    if (!productGridContainer) return;

    const loader = root.querySelector('[data-batch-search-loader]');
    if (loader) loader.hidden = !loading;
    productGridContainer.hidden = loading;
    productGridContainer.toggleAttribute('aria-busy', loading);
    productGridContainer.querySelector('.products')?.classList.toggle('loading', loading);
  }

  function toggleBatchOnlyControls(root, hidden) {
    const controls = [
      root.querySelector('facetfiltersformmobile'),
      root.querySelector('facet-filters-form.facets--bar'),
    ];

    controls.forEach((control) => {
      if (control) control.hidden = hidden;
    });
  }

  function updateResultCount(root, count) {
    const template =
      (count === 1 ? root.dataset.resultCountTemplateOne : root.dataset.resultCountTemplateOther) || '__COUNT__';
    const countText = template.replace('__COUNT__', String(count));

    document.querySelectorAll('.search-filter-count').forEach((element) => {
      element.textContent = countText;
    });

    window.__SEARCH_RESULTS_COUNT__ = count;
  }

  function revealProductImages(container) {
    if (window.lazySizes?.loader) {
      container.querySelectorAll('.lazyload').forEach((image) => {
        window.lazySizes.loader.unveil(image);
      });
    }

    if (window.gsap && window.ScrollTrigger) {
      const images = container.querySelectorAll('.product-card--image-primary');
      if (images.length) {
        window.gsap.to(images, {
          scale: 1,
          opacity: 1,
          stagger: 0.1,
          ease: window.theme?.settings?.animation_easing,
        });
      }
    }
  }

  function renderBatchProducts(root, products, sourceGrid) {
    const target = root.querySelector('#ProductGridContainer');
    if (!target || !sourceGrid) return false;

    const grid = sourceGrid.cloneNode(false);
    grid.innerHTML = '';
    grid.setAttribute('data-no-next', '');
    grid.classList.remove('loading');

    products.forEach((product) => {
      grid.appendChild(product.cloneNode(true));
    });

    const content = document.createElement('div');
    content.appendChild(grid);

    const sidebar = document.createElement('div');
    sidebar.className = 'sidebar-container facets--sidebar-false facets--sidebar-open sticky-scroller--parent';
    sidebar.appendChild(content);

    target.replaceChildren(sidebar);
    setBatchSearchLoading(root, false);
    revealProductImages(target);
    return true;
  }

  function renderBatchEmpty(root, query, sourceEmpty) {
    const target = root.querySelector('#ProductGridContainer');
    if (!target || !sourceEmpty) return false;

    const empty = sourceEmpty.cloneNode(true);
    const message = empty.querySelector('p');
    const template = root.dataset.noResultsTemplate || "No results found for '__QUERY__'.";

    if (message) message.textContent = template.replace('__QUERY__', query);

    target.replaceChildren(empty);
    setBatchSearchLoading(root, false);
    return true;
  }

  async function fetchSearchSection(term, sectionId) {
    const url = new URL(window.theme?.routes?.search_url || window.location.pathname, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', term);

    const response = await fetch(url.toString(), {
      headers: { 'Cache-Control': 'public' },
    });

    if (!response.ok) throw new Error(`Batch search failed with status ${response.status}`);

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  async function initializeBatchSearchPage() {
    const root = document.querySelector('[data-batch-search-root]');
    if (!root) return;

    const currentUrl = new URL(window.location.href);
    const query = currentUrl.searchParams.get('q') || '';
    const terms = parseTerms(query);
    if (terms.length < 2) return;
    if (isNativeScopedQuery(terms)) return;

    if (currentUrl.searchParams.get('batch') !== '1') {
      const nativeQuery = await resolveNativeQuery(terms);
      if (nativeQuery) {
        window.location.replace(
          createSearchUrl(window.theme?.routes?.search_url || currentUrl.pathname, nativeQuery).toString()
        );
        return;
      }
    }

    const sectionId = root.dataset.sectionId || root.querySelector('#product-grid')?.dataset.id;
    if (!sectionId) return;

    root.dataset.batchSearchActive = 'true';
    toggleBatchOnlyControls(root, true);
    setBatchSearchLoading(root, true);

    const responses = await mapWithConcurrency(uniqueTerms(terms), (term) => fetchSearchSection(term, sectionId));
    const documents = responses
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value);

    if (!documents.length) {
      toggleBatchOnlyControls(root, false);
      setBatchSearchLoading(root, false);
      return;
    }

    const products = collectUniqueElements(documents, '#product-grid.products > .products--column');
    const sourceGrid = documents.map((doc) => doc.querySelector('#product-grid.products')).find(Boolean);

    if (products.length) {
      renderBatchProducts(root, products, sourceGrid);
    } else {
      const sourceEmpty = documents.map((doc) => doc.querySelector('#product-grid.collection-empty')).find(Boolean);
      if (!renderBatchEmpty(root, query, sourceEmpty)) setBatchSearchLoading(root, false);
    }

    updateResultCount(root, products.length);
    document.dispatchEvent(
      new CustomEvent('batch-search:rendered', {
        detail: { query, terms, count: products.length },
      })
    );
  }

  window.ThemeBatchSearch = Object.freeze({
    collectUniqueElements,
    createSearchUrl,
    getProductKey,
    isNativeScopedQuery,
    mapWithConcurrency,
    parseTerms,
    resolveNativeQuery,
    uniqueTerms,
  });

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement) || form.getAttribute('role') !== 'search') return;

      const input = form.querySelector('input[name="q"]');
      const query = input?.value.trim() || '';
      if (parseTerms(query).length < 2) return;

      event.preventDefault();
      event.stopPropagation();
      form.setAttribute('aria-busy', 'true');

      const nativeQuery = await resolveNativeQuery(parseTerms(query));
      const url = nativeQuery
        ? createSearchUrl(form.action, nativeQuery)
        : createSearchUrl(form.action, query, true);

      window.location.assign(url.toString());
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBatchSearchPage, { once: true });
  } else {
    initializeBatchSearchPage();
  }
})();
